import React from 'react'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import IconAdd from '@mui/icons-material/Add'
import IconRemove from '@mui/icons-material/Remove'
import { useProductValidation } from '../hooks/useProductValidation'

/**
 * Sélecteur de quantités de produits.
 *
 * Ancienne version : un tableau à 3 colonnes dont la cellule « Quantité »
 * imbriquait une grille de 3 sous-colonnes ; les boutons +/- tombaient sous
 * les 40px de côté et l'un des deux réagissait via un `onClick` posé sur la
 * cellule plutôt que sur le bouton. Ici chaque produit est une ligne à part
 * entière avec un pas de quantité de 48px minimum, utilisable avec des gants.
 */
export const ProductQuantityRow = ({ product, onAdd, onRemove }) => {
  const name = (() => {
    try {
      return JSON.parse(product.ItemName).fr
    } catch {
      return product.ItemName
    }
  })()

  const atMin = product.QuantityToConsume <= 0
  const atMax = product.QuantityToConsume >= product.Quantity

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 2,
        py: 1.5,
        '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ lineHeight: 1.3 }}>
          {name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {product.Quantity} restant{product.Quantity > 1 ? 's' : ''}
        </Typography>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        sx={{
          flexShrink: 0,
          borderRadius: '14px',
          bgcolor: 'rgba(255,255,255,0.06)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton
          onClick={() => onRemove(product)}
          disabled={atMin}
          aria-label={`Retirer une unité de ${name}`}
          sx={{ borderRadius: '14px' }}
        >
          <IconRemove />
        </IconButton>
        <Typography
          variant="h6"
          aria-live="polite"
          sx={{ minWidth: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
        >
          {product.QuantityToConsume}
        </Typography>
        <IconButton
          onClick={() => onAdd(product)}
          disabled={atMax}
          aria-label={`Ajouter une unité de ${name}`}
          sx={{ borderRadius: '14px' }}
        >
          <IconAdd />
        </IconButton>
      </Stack>
    </Stack>
  )
}

const ProductsSelectionTable = () => {
  const { productList, addProductQuantity, removeProductQuantity, resetProductQuantity } =
    useProductValidation()

  return (
    <Box>
      {productList.map((row) => (
        <ProductQuantityRow
          key={row.Id}
          product={row}
          onAdd={addProductQuantity}
          onRemove={removeProductQuantity}
        />
      ))}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button color="error" size="small" onClick={() => resetProductQuantity()}>
          Tout retirer
        </Button>
      </Box>
    </Box>
  )
}

export default ProductsSelectionTable
